import { Buffer } from "buffer";
import React from "react";
import { Link } from "react-router-dom";
import MainLayout from "../layout/MainLayout";
import { motion } from "framer-motion";
import matter from "gray-matter";
import { Calendar, Tag } from "lucide-react";

window.Buffer = Buffer;

// Dynamically import markdown files
const importAll = (r) => {
  return r.keys().map((fileName) => {
    const slug = fileName.replace("./", "").replace(".md", "");
    const file = r(fileName);
    return { slug, file };
  });
};

const posts = importAll(require.context("../blog", false, /\.md$/)).map(
  (post) =>
    fetch(post.file)
      .then((res) => res.text())
      .then((content) => {
        const { data } = matter(content);
        return { slug: post.slug, ...data };
      })
);

export const Blog = () => {
  const [postData, setPostData] = React.useState([]);

  React.useEffect(() => {
    Promise.all(posts).then((data) => {
      const sorted = data.sort(
        (a, b) => new Date(b.date || 0) - new Date(a.date || 0)
      );
      setPostData(sorted);
    });
  }, []);

  if (!postData || postData.length === 0) {
    return (
      <MainLayout>
        <div className="text-center py-24">
          <h2 className="text-2xl font-bold mb-2">No blog posts yet.</h2>
          <p className="text-gray-500">Check back soon for updates!</p>
        </div>
      </MainLayout>
    );
  }

  const featuredPost = postData[0];
  const otherPosts = postData.slice(1);

  return (
    <MainLayout>
      {/* Hero Section */}
      <section
        className="relative h-[60vh] rounded-3xl mx-4 sm:mx-10 my-6 overflow-hidden shadow-xl flex items-center justify-center bg-cover bg-center"
        style={{
          backgroundImage: `url(${featuredPost.cover || "/images/fallback.jpg"})`,
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 to-black/80 z-0" />
        <div className="relative z-10 max-w-3xl text-center text-white px-6">
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-4xl sm:text-5xl font-bold mb-4 just-me-again-down-here-regular"
          >
            Our Blog
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-lg text-white/90 mb-4"
          >
            Insights, tips, and stories to inspire sustainable travel across Uganda and East Africa.
          </motion.p>
          <Link
            to={`/blog/${featuredPost.slug}`}
            className="inline-block px-6 py-3 bg-teal-600 hover:bg-teal-500 rounded-lg font-semibold transition"
          >
            {featuredPost.title}
          </Link>
        </div>
      </section>

      {/* Blog List */}
      <section className="max-w-7xl mx-auto px-4 pb-24 grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
        {otherPosts.map((post, index) => (
          <motion.div
            key={post.slug}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.05 }}
            className="relative group rounded-2xl overflow-hidden shadow-lg hover:scale-[1.02] transition-all duration-300"
          >
            <div
              className="h-64 bg-cover bg-center"
              style={{
                backgroundImage: `url(${post.cover || "/images/fallback.jpg"})`,
              }}
            >
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-black/20 opacity-0 group-hover:opacity-100 transition-opacity" />
            </div>
            <div className="absolute top-0 left-0 w-full h-full flex flex-col justify-end p-5 text-white z-10">
              {post.category && (
                <span className="inline-flex items-center text-xs font-medium bg-teal-600 rounded-full px-3 py-1 mb-2 w-fit">
                  <Tag size={14} className="mr-1" />
                  {post.category}
                </span>
              )}
              <h3 className="text-xl font-bold mb-2">{post.title}</h3>
              <p className="text-sm text-white/80 mb-3 line-clamp-2">
                {post.excerpt}
              </p>
              <div className="flex items-center justify-between text-xs text-white/60">
                {post.date && (
                  <div className="flex items-center gap-1">
                    <Calendar size={14} />
                    {new Date(post.date).toLocaleDateString()}
                  </div>
                )}
                <Link
                  to={`/blog/${post.slug}`}
                  className="font-semibold hover:underline text-teal-400"
                >
                  Read More →
                </Link>
              </div>
            </div>
          </motion.div>
        ))}
      </section>
    </MainLayout>
  );
};

export default Blog;
